import React from 'react'
import { useDebounce } from 'hooks'
import { getOrder, filterAndOrder } from './utils'
import type { CardTableOrder } from './CardTableHeaderCell'

type UseCardTableStateOptions = {
  rowId?: string
  searchByKeys?: string[]
  selected?: string[]
  data?: Record<string, any>[]
  searchBy?: string
  onValueChange?: (selected: string[]) => void
}

export const useCardTableState = ({
  rowId,
  searchByKeys,
  selected = [],
  data = [],
  searchBy,
  onValueChange,
}: UseCardTableStateOptions) => {
  const debouncedSearchBy = useDebounce(searchBy, { delay: 300 })

  const [displayedData, setDisplayedData] = React.useState(data)
  const [order, setOrder] = React.useState<CardTableOrder>({
    column: null,
    ascending: null,
  })

  React.useEffect(() => {
    setDisplayedData(
      filterAndOrder(data, order, debouncedSearchBy, searchByKeys),
    )
  }, [debouncedSearchBy])

  React.useEffect(() => {
    setDisplayedData(
      filterAndOrder(data, order, debouncedSearchBy, searchByKeys),
    )
  }, [data])

  const toggleRowSelection = id =>
    onValueChange?.(
      selected.includes(id)
        ? selected.filter(selectedId => selectedId !== id)
        : [...selected, id],
    )

  const toggleSelectAll = () => {
    const isSomeDeselected = selected.length < data.length

    onValueChange?.(isSomeDeselected ? data.map(row => row[String(rowId)]) : [])
  }

  const updateOrder = id => {
    const newOrder = getOrder(order, id)

    setOrder(newOrder)
    setDisplayedData(
      filterAndOrder(data, newOrder, debouncedSearchBy, searchByKeys),
    )
  }

  const allRowsSelected = data.length > 0 && selected.length === data.length

  return {
    state: {
      order,
      displayedData,
      allRowsSelected,
    },
    handlers: {
      toggleRowSelection,
      toggleSelectAll,
      setOrder: updateOrder,
    },
  }
}
